import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminUsersApi,
  ALL_ROLES,
  type AdminUserSummary,
  type Role,
} from '@/api/adminUsers';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { Checkbox } from '@/components/ui/checkbox';

/// Tela `/users` — gestão de usuários pelo super-admin. Gateada por
/// `AuthGuard requireRole="verified_organizer"` em App.tsx. Organizers
/// comuns não veem o link nem conseguem chamar os endpoints (defesa em
/// profundidade — backend tem RequireVerifiedOrganizer policy também).
///
/// Fluxo MVP (sem envio de email):
/// 1. Admin digita email + nome do cliente
/// 2. Escolhe roles iniciais (default: organizer)
/// 3. Submit → backend cria user + gera magic link via Firebase Admin SDK
/// 4. Modal mostra o link com botões "Copiar" + "Enviar pelo WhatsApp"
/// 5. Admin manda manualmente — cliente clica, autentica direto, vê /events
///
/// Idempotência: se email já existe, backend reusa (sem sobrescrever roles)
/// e devolve novo magic link mesmo assim. UI mostra um aviso quando isso
/// acontece (`user.roles` veio diferente do `initialRoles` pedido).
export function UsersPage() {
  const qc = useQueryClient();

  // ─── busca por email ────────────────────────────────────────────
  const [searchEmail, setSearchEmail] = useState('');
  const [searchTriggered, setSearchTriggered] = useState('');
  const search = useQuery({
    queryKey: ['admin-users', 'search', searchTriggered],
    queryFn: () => adminUsersApi.searchByEmail(searchTriggered),
    enabled: searchTriggered.length > 0,
  });

  // ─── invite ──────────────────────────────────────────────────────
  const [inviteOpen, setInviteOpen] = useState(false);
  const [magicLinkResult, setMagicLinkResult] = useState<{
    email: string;
    magicLink: string;
    rolesGranted: Role[];
    rolesRequested: Role[];
  } | null>(null);

  const invite = useMutation({
    mutationFn: adminUsersApi.invite,
    onSuccess: (data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      setInviteOpen(false);
      setMagicLinkResult({
        email: data.email,
        magicLink: data.magicLink,
        rolesGranted: data.roles,
        rolesRequested: vars.initialRoles ?? ['organizer'],
      });
    },
  });

  // ─── set roles ───────────────────────────────────────────────────
  const [editRolesFor, setEditRolesFor] = useState<AdminUserSummary | null>(null);
  const setRoles = useMutation({
    mutationFn: ({ uid, roles }: { uid: string; roles: Role[] }) =>
      adminUsersApi.setRoles(uid, roles),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      setEditRolesFor(null);
    },
  });

  // ─── disable ─────────────────────────────────────────────────────
  const disable = useMutation({
    mutationFn: adminUsersApi.disable,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });
  const [confirmDisable, setConfirmDisable] = useState<{ uid: string; email: string } | null>(null);

  return (
    <div>
      {/* #936 — "← Eventos", e-mail e Sair foram pro menu lateral. */}
      <header className="border-b">
        <div className="container mx-auto py-3 sm:py-4">
          <h1 className="text-lg sm:text-xl font-semibold">Usuários</h1>
        </div>
      </header>

      <main className="container mx-auto py-6 sm:py-10 space-y-6 max-w-3xl">
        <Card>
          <CardHeader>
            <CardTitle>Convidar novo usuário</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Cria conta no Firebase, atribui a role e gera um link de acesso direto.
              Você copia o link e envia pelo WhatsApp/email do cliente. Ele clica e
              entra autenticado, sem senha.
            </p>
            <Button
              onClick={() => setInviteOpen(true)}
              className="w-full sm:w-auto min-h-[44px]"
            >
              Novo convite
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Buscar por email</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setSearchTriggered(searchEmail.trim().toLowerCase());
              }}
              className="flex flex-col sm:flex-row gap-2"
            >
              <Input
                type="email"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                placeholder="cliente@exemplo.com"
                className="flex-1 min-h-[44px]"
                autoComplete="off"
              />
              <Button type="submit" className="min-h-[44px] sm:w-32">
                Buscar
              </Button>
            </form>

            {search.isLoading && (
              <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
                <Spinner className="h-4 w-4" /> Procurando…
              </div>
            )}

            {search.isError && (
              <p className="mt-4 text-sm text-destructive">
                Erro ao buscar. Verifique o email e tente novamente.
              </p>
            )}

            {search.isSuccess && !search.data && searchTriggered && (
              <div className="mt-4 rounded-md border border-dashed p-4">
                <p className="text-sm">
                  Nenhum usuário com email <code>{searchTriggered}</code>.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => setInviteOpen(true)}
                >
                  Convidar este email
                </Button>
              </div>
            )}

            {search.data && (
              <div className="mt-4">
                <UserRow
                  user={search.data}
                  onEditRoles={() => setEditRolesFor(search.data!)}
                  onDisable={() =>
                    setConfirmDisable({
                      uid: search.data!.uid,
                      email: search.data!.email,
                    })
                  }
                  disablePending={
                    disable.isPending && disable.variables === search.data.uid
                  }
                />
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Modais */}
      {inviteOpen && (
        <InviteModal
          onSubmit={(req) => invite.mutate(req)}
          onClose={() => setInviteOpen(false)}
          pending={invite.isPending}
          error={invite.error}
        />
      )}
      {magicLinkResult && (
        <MagicLinkModal
          result={magicLinkResult}
          onClose={() => setMagicLinkResult(null)}
        />
      )}
      {editRolesFor && (
        <EditRolesModal
          user={editRolesFor}
          onSubmit={(roles) => setRoles.mutate({ uid: editRolesFor.uid, roles })}
          onClose={() => setEditRolesFor(null)}
          pending={setRoles.isPending}
        />
      )}
      <ConfirmDialog
        open={confirmDisable != null}
        onOpenChange={(o) => !o && setConfirmDisable(null)}
        title={`Desabilitar ${confirmDisable?.email ?? ''}?`}
        description="O usuário não poderá mais fazer login."
        confirmLabel="Desabilitar"
        destructive
        onConfirm={() => confirmDisable && disable.mutate(confirmDisable.uid)}
      />
    </div>
  );
}

// ─── linha de usuário ──────────────────────────────────────────────

function UserRow({
  user,
  onEditRoles,
  onDisable,
  disablePending,
}: {
  user: AdminUserSummary;
  onEditRoles: () => void;
  onDisable: () => void;
  disablePending: boolean;
}) {
  return (
    <div className="border rounded-md p-4 space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium truncate">{user.displayName ?? '—'}</p>
          <p className="text-sm text-muted-foreground truncate">{user.email}</p>
          <p className="text-xs text-muted-foreground mt-0.5 font-mono truncate">
            uid: {user.uid}
          </p>
        </div>
        {user.disabled && (
          <span className="text-xs rounded bg-destructive/15 text-destructive px-2 py-1 self-start">
            DESABILITADO
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {user.roles.length === 0 ? (
          <span className="text-xs text-muted-foreground italic">sem roles</span>
        ) : (
          user.roles.map((r) => (
            <span
              key={r}
              className="text-xs rounded-full bg-muted px-2 py-0.5 font-mono"
            >
              {r}
            </span>
          ))
        )}
      </div>

      <div className="flex flex-wrap gap-2 pt-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onEditRoles}
          className="min-h-[40px]"
        >
          Editar roles
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDisable}
          disabled={disablePending || user.disabled}
          className="min-h-[40px] text-destructive hover:text-destructive"
        >
          {disablePending ? 'Desabilitando…' : 'Desabilitar'}
        </Button>
      </div>
    </div>
  );
}

// ─── modal: novo convite ───────────────────────────────────────────

function InviteModal({
  onSubmit,
  onClose,
  pending,
  error,
}: {
  onSubmit: (req: {
    email: string;
    name?: string;
    initialRoles?: Role[];
  }) => void;
  onClose: () => void;
  pending: boolean;
  error: unknown;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<Set<Role>>(
    new Set<Role>(['organizer']),
  );

  const toggle = (r: Role) => {
    const next = new Set(selectedRoles);
    if (next.has(r)) next.delete(r);
    else next.add(r);
    setSelectedRoles(next);
  };

  const canSubmit = email.includes('@') && selectedRoles.size > 0 && !pending;

  return (
    <ModalShell onClose={onClose} title="Novo convite">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          onSubmit({
            email: email.trim(),
            name: name.trim() || undefined,
            initialRoles: Array.from(selectedRoles),
          });
        }}
        className="space-y-4"
      >
        <div className="space-y-1.5">
          <Label htmlFor="invite-email">Email *</Label>
          <Input
            id="invite-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="min-h-[44px]"
            autoComplete="off"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="invite-name">Nome (opcional)</Label>
          <Input
            id="invite-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="min-h-[44px]"
            autoComplete="off"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Roles iniciais</Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {ALL_ROLES.map((r) => (
              <label
                key={r}
                className="flex items-center gap-2 cursor-pointer text-sm select-none"
              >
                <Checkbox
                  checked={selectedRoles.has(r)}
                  onCheckedChange={() => toggle(r)}
                />
                <span className="font-mono">{r}</span>
              </label>
            ))}
          </div>
        </div>

        {error != null && (
          <p className="text-sm text-destructive">
            {error instanceof Error ? error.message : 'Erro ao criar convite.'}
          </p>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className="min-h-[44px]"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={!canSubmit}
            className="min-h-[44px] sm:min-w-32"
          >
            {pending ? 'Criando…' : 'Criar convite'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ─── modal: magic link gerado ──────────────────────────────────────

function MagicLinkModal({
  result,
  onClose,
}: {
  result: {
    email: string;
    magicLink: string;
    rolesGranted: Role[];
    rolesRequested: Role[];
  };
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(result.magicLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copie o link:', result.magicLink);
    }
  };

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
    `Olá! Use o link abaixo pra entrar no WindMaps Admin (sem precisar de senha):\n${result.magicLink}`,
  )}`;

  // Idempotência: se as roles devolvidas não batem com as pedidas, sinal de
  // que o user já existia com roles diferentes. Avisar admin pra que ele saiba
  // se precisa rodar "Editar roles" depois.
  const reusedExistingUser =
    result.rolesGranted.length !== result.rolesRequested.length ||
    !result.rolesRequested.every((r) => result.rolesGranted.includes(r));

  return (
    <ModalShell onClose={onClose} title="Link de acesso pronto">
      <div className="space-y-4">
        <p className="text-sm">
          Convite criado para <strong>{result.email}</strong>. Envie o link
          abaixo pelo seu canal de atendimento. O cliente clica e entra
          autenticado sem precisar de senha.
        </p>

        {reusedExistingUser && (
          <div className="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-300">
            ⚠ Email já existia. Roles foram preservadas (
            <code>{result.rolesGranted.join(', ') || 'nenhuma'}</code>) em vez de
            sobrescrever pelo que você pediu. Use “Editar roles” se quiser
            ajustar.
          </div>
        )}

        <div className="rounded border bg-muted/50 p-3">
          <p className="text-xs font-mono break-all">{result.magicLink}</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={copyLink} className="min-h-[44px] flex-1">
            {copied ? 'Copiado ✓' : 'Copiar link'}
          </Button>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1"
          >
            <Button variant="outline" className="min-h-[44px] w-full">
              Enviar pelo WhatsApp
            </Button>
          </a>
        </div>

        <div className="pt-2 text-right">
          <Button variant="ghost" onClick={onClose} className="min-h-[40px]">
            Fechar
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

// ─── modal: editar roles ────────────────────────────────────────────

function EditRolesModal({
  user,
  onSubmit,
  onClose,
  pending,
}: {
  user: AdminUserSummary;
  onSubmit: (roles: Role[]) => void;
  onClose: () => void;
  pending: boolean;
}) {
  const [selected, setSelected] = useState<Set<Role>>(new Set(user.roles));
  const toggle = (r: Role) => {
    const next = new Set(selected);
    if (next.has(r)) next.delete(r);
    else next.add(r);
    setSelected(next);
  };

  return (
    <ModalShell onClose={onClose} title={`Editar roles: ${user.email}`}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(Array.from(selected));
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {ALL_ROLES.map((r) => (
            <label
              key={r}
              className="flex items-center gap-2 cursor-pointer text-sm select-none"
            >
              <Checkbox
                checked={selected.has(r)}
                onCheckedChange={() => toggle(r)}
              />
              <span className="font-mono">{r}</span>
            </label>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          As mudanças só aparecem no JWT do usuário na próxima renovação do
          token (~1h). Para refletir imediato no dispositivo do cliente, ele
          precisa fazer logout/login.
        </p>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className="min-h-[44px]"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={pending}
            className="min-h-[44px] sm:min-w-32"
          >
            {pending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ─── shell de modal ────────────────────────────────────────────────

function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-background rounded-t-xl sm:rounded-lg w-full sm:max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b px-4 sm:px-6 py-3 flex items-center justify-between sticky top-0 bg-background z-10">
          <h2 className="text-base sm:text-lg font-semibold pr-2">{title}</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="min-h-[40px] min-w-[40px]"
            aria-label="Fechar"
          >
            ×
          </Button>
        </div>
        <div className="px-4 sm:px-6 py-4">{children}</div>
      </div>
    </div>
  );
}
